import React, { useEffect, useState } from "react";
import { 
    Container,
    Grid,
    Box,
    Typography,
    Button,
    Chip,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Stack,
    Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import { ArrowBack } from "@mui/icons-material";
import { formatCurrency } from "../capital/capitalUtils";
import OptimizedAgentOpportunityForm from "../components/OptimizedAgentOpportunityForm";

const OpportunitiesList = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);

    useEffect(() => {
        loadOpportunities();
    }, []);

    const loadOpportunities = async () => {
        try {
            setLoading(true);
            const data = await opportunitiesService.getActiveOpportunities();
            setOpportunities(data);
        } catch (error) {
            // Error loading opportunities
            // setError("Failed to load opportunities"); // This line was removed
        } finally {
            setLoading(false);
        }
    };

    const handleLearnMore = (opportunityId) => {
        navigate(`/opportunity/${opportunityId}`);
    };

    const getRiskColor = (riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
        case "low": return "success";
        case "medium": return "warning";
        case "high": return "error";
        default: return "default";
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography>Loading opportunities...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={4}>
                <Box>
                    <Button 
                        startIcon={<ArrowBack />}
                        variant="text" 
                        onClick={() => navigate("/dashboard")}
                        sx={{ mb: 2 }}
                    >
            Back to Dashboard
                    </Button>
                    <Box textAlign="center">
                        <Typography variant="h2" color="primary" sx={{ fontWeight: "bold", mb: 2 }}>
              Investment Opportunities
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
              Discover exclusive real estate investment opportunities across Spain
                        </Typography>
                    </Box>
                    <Box textAlign="right" sx={{ mt: 2 }}>
                        <Button variant="contained" color="primary" onClick={() => setFormOpen(true)}>
              Nueva Oportunidad
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {opportunities.map((opportunity) => (
                        <Grid item xs={12} md={6} lg={4} key={opportunity.id} sx={{ display: "flex" }}>
                            <Card sx={{ 
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 8px 32px 0 rgba(21, 82, 99, 0.15)",
                                },
                            }}>
                                <Box sx={{ 
                                    position: "relative",
                                    height: 200,
                                    flexShrink: 0,
                                }}>
                                    {opportunity.media && opportunity.media.length > 0 ? (
                                        opportunity.media[0].type === "video" ? (
                                            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                                                <video
                                                    src={opportunity.media[0].url}
                                                    height="200"
                                                    style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: 4 }}
                                                    controls={false}
                                                    muted
                                                    preload="metadata"
                                                />
                                                <Box sx={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.5)", color: "white", px: 1, borderRadius: 1, fontSize: 12 }}>
                          ▶ Video
                                                </Box>
                                            </Box>
                                        ) : (
                                            <img
                                                src={opportunity.media[0].url}
                                                alt={opportunity.title}
                                                height="200"
                                                style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: 4 }}
                                            />
                                        )
                                    ) : (
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={opportunity.images?.[0] || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlByb3BlcnR5IEltYWdlPC90ZXh0Pgo8L3N2Zz4K"}
                                            alt={opportunity.title}
                                            sx={{ objectFit: "cover" }}
                                            onError={(e) => {
                                                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIExvYWRpbmc8L3RleHQ+Cjwvc3ZnPgo=";
                                            }}
                                        />
                                    )}
                                    <Chip
                                        label={`${opportunity.riskLevel} Risk`}
                                        color={getRiskColor(opportunity.riskLevel)}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                        }}
                                    />
                                </Box>

                                <CardContent sx={{ 
                                    flex: "1 1 auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 2,
                                    flexGrow: 1,
                                }}>
                                    <Stack spacing={2} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                        <Box>
                                            <Typography 
                                                variant="h6" 
                                                color="primary" 
                                                sx={{ 
                                                    fontWeight: "bold",
                                                    mb: 1,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    lineHeight: 1.25,
                                                    height: "2.5em",
                                                }}
                                            >
                                                {opportunity.title}
                                            </Typography>
                                            <Chip 
                                                label={opportunity.propertyType}
                                                color="primary"
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />
                                        </Box>

                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{ 
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                lineHeight: 1.4,
                                                height: "4.2em",
                                            }}
                                        >
                                            {opportunity.description}
                                        </Typography>

                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ 
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                height: "1.5em",
                                                flexShrink: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                      📍 {opportunity.location}
                                        </Typography>

                                        <Box sx={{ flexGrow: 1 }} />
                                        <Divider />

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                                                <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>
                                                    {formatCurrency(opportunity.amount ?? 0, "EUR", i18n.language)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Min Investment</Typography>
                                                <Typography variant="h6" color="success.main" sx={{ fontWeight: "bold" }}>
                                                    {formatCurrency(opportunity.minimumInvestment ?? 0, "EUR", i18n.language)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                                <Typography variant="h6" color="secondary" sx={{ fontWeight: "bold" }}>
                                                    {opportunity.expectedReturn}%
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Duration</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                    {opportunity.duration} months
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </CardContent>

                                <CardActions sx={{ 
                                    p: 2,
                                    pt: 0,
                                    justifyContent: "space-between",
                                    flexShrink: 0
                                }}>
                                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                        <Button 
                                            variant="outlined" 
                                            color="primary" 
                                            size="small"
                                            onClick={() => handleLearnMore(opportunity.id)}
                                            sx={{ flex: 1, mr: 1 }}
                                        >
                      View Details
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            size="small"
                                            onClick={() => handleLearnMore(opportunity.id)}
                                            sx={{ flex: 1 }}
                                        >
                      Invest Now
                                        </Button>
                                    </Stack>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Box textAlign="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
            Showing {opportunities.length} investment opportunities
                    </Typography>
                </Box>
            </Stack>
            <OptimizedAgentOpportunityForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSuccess={() => {
                    setFormOpen(false);
                    loadOpportunities();
                }}
            />
        </Container>
    );
};

export default OpportunitiesList; 